﻿import {translation} from "@2d-front-monorepo/core";
import {LinkButton, Message, PopOver} from "@2d-front-monorepo/design-library";
import {
	formatDate, formatTime,
	InstallationByHostData, ReservationResult,
	ReservationStatusComponent,
	ReservationStatusType,
	Tariffs
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import React, {JSX, ReactNode} from "react";
import {API_ENDPOINT} from "../../../api-endpoint";
import {FilteredItemsList} from "../../../components/Filters/FilteredItemsList";
import {FilteredItemsProvider} from "../../../components/Filters/FilteredItemsProvider";
import {PermitsState} from "../../../contexts/PermitsContext";
import Registrations, {DefaultReservationProps} from "../../../customers/Registrations";
import {getGlobalContext} from "../../../hooks/getGlobalContext";
import {formatRoute, PrivateRoutes} from "../../../routes";
import {ReservationTotalComponent} from "./ReservationTotalComponent";

type ReservationProps = DefaultReservationProps & {
	menu?: ReactNode;
	footer?: ReactNode;
}

function ReservationQuoted({reservation}: { reservation: ReservationResult }): JSX.Element {
	const {installation} = getGlobalContext();

	return (
		<div className={"padded inline"}>
			<div className="layout horizontal aligned middle form-message form-message--information">
				<span className="flex">{translation(PermitTranslations.summary_page__quoted_reservation)}</span>

				<a href={formatRoute(API_ENDPOINT.reservationQuote, reservation.id, installation.id)} target="_blank"
				   className={"form__button form__button--small"}>{translation(PermitTranslations.download_reservation_quote)}</a>&nbsp;
				<LinkButton small={true} kind={"primary"} to={formatRoute(PrivateRoutes.reservationApprovement, reservation.id)}>
					{translation(PermitTranslations.summary_page__approve_reservation)}
				</LinkButton>
			</div>
		</div>
	);
}

function Reservation(props: ReservationProps): JSX.Element {
	const {tariff, menu, footer, reservation} = props;

	return (
		<section className="card">
			<header className="layout horizontal aligned middle" id={"p_" + reservation.id}>
				<div className="flex">
					<div className="grid">
						<dl className="permit__date col-1of2">
							<dt>{translation(PermitTranslations.start_date)}</dt>
							<dd>
								<span>{formatDate(reservation.startDate.toDateOnly())}</span>
								<span>{formatTime(reservation.startDate.toTimeOnly())}</span>
							</dd>
						</dl>
						<dl className="permit__date col-1of2">
							<dt>{translation(PermitTranslations.end_date)}</dt>
							<dd>
								<span>{formatDate(reservation.endDate.toDateOnly())}</span>
								<span>{formatTime(reservation.endDate.toTimeOnly())}</span>
							</dd>
						</dl>
					</div>
				</div>
				<>
					<ReservationStatusComponent reservation={reservation}/>
					<div><PopOver>{menu}</PopOver></div>
				</>
			</header>
			<hr className="no-margin"/>
			<div className="grid padded">
				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.reservation_tariff)}</dt>
					<dd>{tariff.title}</dd>
				</dl>
				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.purpose)}</dt>
					<dd>{reservation.purpose}</dd>
				</dl>
				{/*<dl className="title-desc col-1of4">*/}
				{/*	<dt>{translation(PermitTranslations.reservation_type)}</dt>*/}
				{/*	<dd>{translation(reservationTypeLabel(reservation.type))}</dd>*/}
				{/*</dl>*/}

				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.streets)}</dt>
					<dd>{reservation.blocks.length}</dd>
				</dl>
				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.meters)}</dt>
					<dd>{reservation.meters.length}</dd>
				</dl>
				{footer}
			</div>
			{reservation.status.type === ReservationStatusType.Quoted && <ReservationQuoted reservation={reservation}/>}
			<ReservationTotalComponent reservation={reservation}/>
		</section>);
}

export default function ReservationListComponent(props: PermitsState & {
	installation: InstallationByHostData,
	filtered: ReservationResult[]
}): JSX.Element {
	const MenuComponent = Registrations.getReservationMenu();
	const FooterComponent = Registrations.getReservationFooter();
	const {filtered, installation} = props;

	if (filtered.length === 0) {
		return <Message title={{text: translation(PermitTranslations.summary_page__no_reservations)}} type="information"/>;
	}

	return <div className="layout vertical gap">
		<FilteredItemsProvider items={filtered}>
			<FilteredItemsList
				keyExtractor={(reservation: ReservationResult) => reservation.id}
				renderItem={(reservation: ReservationResult) => {
					const tariff = Tariffs.get(installation, reservation.tariffId);

					return <Reservation key={reservation.id}
					                    reservation={reservation}
					                    reservations={props}
					                    tariff={tariff}
					                    now={installation.now}
					                    footer={<FooterComponent reservation={reservation}
					                                             reservations={props}
					                                             tariff={tariff}
					                                             now={installation.now}/>}
					                    menu={<MenuComponent reservation={reservation}
					                                         reservations={props}
					                                         tariff={tariff}
					                                         now={installation.now}/>}
					/>
				}}/>
		</FilteredItemsProvider>
	</div>;
}
